import { userApi } from '../../utils/urls';
import { rootApi } from '../../services/rootApi';

export const usersApiSlice = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getTeachers: builder.query({
      query: () => ({
        url: userApi(),
        method: 'POST',
        body: {
          method: 'prepareForUserList'
        }
      }),
      providesTags: ['Users'],
      transformResponse: (response) => {
        let data = response;
        if (response && Array.isArray(response.data)) {
          data = response.data;
        }
        if (Array.isArray(data)) {
          return data.filter((user) => user.roles.includes('teacher'));
        }
        return [];
      }
    }),

    getReaders: builder.query({
      query: ({ pageLength, searchText, sortBy = 'name', sortMode = 'asc' } = {}) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: {
            showCourses: true,
            showCircles: true,
            showUncorrected: false,
            searchText,
            sortBy,
            sortMode,
          },
          pageStart: 0,
          pageLength,
          method: 'findReaders'
        }
      }),
      providesTags: ['Users'],
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      }
    }),

    getReadersMinimal: builder.query({
      query: () => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: {
            sortBy: 'name',
            sortMode: 'asc'
          },
          method: 'findReaders'
        }
      }),
      providesTags: ['Users'],
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      }
    }),

    getUsersByCourseId: builder.query({
      query: ({ courseId, token, sortBy = 'name', sortMode = 'asc' }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: {
            sortBy,
            sortMode,
            courseId,
            token,
          },
          method: 'findByCourseId'
        }
      }),
      providesTags: (result, error, { courseId }) => [{ type: 'CourseUsers', id: courseId }],
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      }
    }),

    getUsersByGroupId: builder.query({
      query: ({ circleId, sortBy, sortMode }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: {
            circleId,
            sortMode,
            sortBy
          },
          method: 'findByCircleId'
        }
      }),
      providesTags: (result, error, { circleId }) => [{ type: 'GroupUsers', id: circleId }],
      transformResponse: (response, meta, arg) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      }
    }),

    removeUserFromCourse: builder.mutation({
      query: ({ userId, courseId }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: {
            courseId,
            userId
          },
          method: 'removeFromCourse'
        }
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'CourseUsers', id: courseId }],
    }),

    addUserToGroup: builder.mutation({
      query: ({ userId, circleId }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: { userId, circleId },
          method: 'addToCircle'
        }
      }),
      invalidatesTags: (result, error, { circleId }) => [{ type: 'GroupUsers', id: circleId }],
      transformErrorResponse: (response) => {
        if (response.status === 'PARSING_ERROR' && response.data && typeof response.data === 'string') {
          return { error: 'Server returned invalid response format' };
        }
        return response;
      },
    }),

    removeUserFromGroup: builder.mutation({
      query: ({ userId, circleId }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: { userId, circleId },
          method: 'removeFromCircle'
        }
      }),
      invalidatesTags: (result, error, { circleId }) => [{ type: 'GroupUsers', id: circleId }],
      transformErrorResponse: (response) => {
        if (response.status === 'PARSING_ERROR' && response.data && typeof response.data === 'string') {
          return { error: 'Server returned invalid response format' };
        }
        return response;
      },
    }),

    addUserToCourse: builder.mutation({
      query: ({ userId, courseId, role }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: { userId, courseId, role },
          method: 'addToCourse'
        }
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'CourseUsers', id: courseId }],
      transformErrorResponse: (response) => {
        if (response.status === 'PARSING_ERROR' && response.data && typeof response.data === 'string') {
          return { error: 'Server returned invalid response format' };
        }
        return response;
      },
    }),

    removeUserFromStore: builder.mutation({
      query: ({ userId }) => ({
        url: userApi(),
        method: 'POST',
        body: {
          data: { id: userId },
          method: 'expel'
        }
      }),
      invalidatesTags: ['Users'],
    })
  }),
  overrideExisting: false,
});

export const {
  useGetTeachersQuery,
  useGetReadersQuery,
  useGetReadersMinimalQuery,
  useGetUsersByCourseIdQuery,
  useGetUsersByGroupIdQuery,
  useRemoveUserFromCourseMutation,
  useAddUserToGroupMutation,
  useRemoveUserFromGroupMutation,
  useAddUserToCourseMutation,
  useRemoveUserFromStoreMutation
} = usersApiSlice;
